<script setup>
import { ref, computed, onMounted } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import MultiSelect from "primevue/multiselect";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import { formatCurrency, formatDate } from "@/util/format";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import InputNumber from "primevue/inputnumber";
import TransactionBatchDetailView from "./TransactionBatchDetailView.vue";
import VendorDetailDialog from "./VendorDetailDialog.vue";
import { dashboardAPI } from "../service/api.js";
import { useToast } from "primevue/usetoast";

const toast = useToast();

// Dialog state
const showBatchDetailDialog = ref(false);
const selectedBatchId = ref(null);
const showVendorDialog = ref(false);
const selectedVendorId = ref(null);

// Data state
const transactions = ref([]);
const totalSum = ref(0);
const loading = ref(false);
const pagination = ref({
  page: 1,
  pageSize: 50,
  totalItems: 0,
  totalPages: 0,
});

// Filter state
const apiFilters = ref({
  customer_id: '',
  customer_name: '',
  status: null,
  payment_type: null,
  carepay_vendor_id: '',
  vendor_name: '',
  employer: '',
  claim_number: '',
  external_trans_id: '',
  confirmation_number: '',
  register_date_from: null,
  register_date_to: null,
  invoice_date_from: null,
  invoice_date_to: null,
  payment_amount_min: null,
  payment_amount_max: null,
  external_batch_id: '',
  vendor_email: '',
  transaction_batch_status: null,
  bank_payment_file_status: null,
  dcn: '',
  eor_link: '',
  payment_processed_date_from: null,
  payment_processed_date_to: null,
  // "Has value" filters
  has_confirmation_number: null,
  has_external_batch_id: null,
  has_vendor_email: null,
  has_dcn: null,
  has_eor_link: null,
  has_carepay_vendor_id: null,
  has_claim_number: null,
  has_external_trans_id: null,
  missing_confirmation: null,
});

// Available filters configuration
const availableFilters = [
  { key: 'status', label: 'Status', icon: 'pi-check-circle', type: 'dropdown' },
  { key: 'payment_type', label: 'Payment Type', icon: 'pi-credit-card', type: 'dropdown' },
  { key: 'carepay_vendor_id', label: 'Vendor ID', icon: 'pi-building', type: 'text' },
  { key: 'vendor_name', label: 'Vendor Name', icon: 'pi-building', type: 'text' },
  { key: 'vendor_email', label: 'Vendor Email', icon: 'pi-envelope', type: 'text' },
  { key: 'employer', label: 'Employer', icon: 'pi-briefcase', type: 'text' },
  { key: 'customer_id', label: 'Customer ID', icon: 'pi-user', type: 'text' },
  { key: 'customer_name', label: 'Customer Name', icon: 'pi-user', type: 'text' },
  { key: 'claim_number', label: 'Claim Number', icon: 'pi-hashtag', type: 'text' },
  { key: 'external_trans_id', label: 'Trans ID', icon: 'pi-id-card', type: 'text' },
  { key: 'external_batch_id', label: 'Payment File ID', icon: 'pi-tag', type: 'text' },
  { key: 'confirmation_number', label: 'Confirmation Number', icon: 'pi-verified', type: 'text' },
  { key: 'transaction_batch_status', label: 'Transaction Batch Status', icon: 'pi-list', type: 'dropdown' },
  { key: 'bank_payment_file_status', label: 'Bank Payment File Status', icon: 'pi-file', type: 'dropdown' },
  { key: 'dcn', label: 'Invoice Number', icon: 'pi-file-edit', type: 'text' },
  { key: 'eor_link', label: 'EOR Link', icon: 'pi-link', type: 'text' },
  { key: 'register_date_from', label: 'Register Date From', icon: 'pi-calendar', type: 'date' },
  { key: 'register_date_to', label: 'Register Date To', icon: 'pi-calendar', type: 'date' },
  { key: 'invoice_date_from', label: 'Invoice Date From', icon: 'pi-calendar', type: 'date' },
  { key: 'invoice_date_to', label: 'Invoice Date To', icon: 'pi-calendar', type: 'date' },
  { key: 'payment_processed_date_from', label: 'Payment Processed From', icon: 'pi-calendar', type: 'date' },
  { key: 'payment_processed_date_to', label: 'Payment Processed To', icon: 'pi-calendar', type: 'date' },
  { key: 'payment_amount_min', label: 'Min Amount', icon: 'pi-dollar', type: 'currency' },
  { key: 'payment_amount_max', label: 'Max Amount', icon: 'pi-dollar', type: 'currency' },
  // "Has value" filters
  { key: 'has_confirmation_number', label: 'Has Confirmation Number', icon: 'pi-check', type: 'boolean' },
  { key: 'has_external_batch_id', label: 'Has Payment File ID', icon: 'pi-check', type: 'boolean' },
  { key: 'has_vendor_email', label: 'Has Vendor Email', icon: 'pi-check', type: 'boolean' },
  { key: 'has_dcn', label: 'Has Invoice Number', icon: 'pi-check', type: 'boolean' },
  { key: 'has_eor_link', label: 'Has EOR Link', icon: 'pi-check', type: 'boolean' },
  { key: 'has_carepay_vendor_id', label: 'Has Vendor ID', icon: 'pi-check', type: 'boolean' },
  { key: 'has_claim_number', label: 'Has Claim Number', icon: 'pi-check', type: 'boolean' },
  { key: 'has_external_trans_id', label: 'Has Trans ID', icon: 'pi-check', type: 'boolean' },
  { key: 'missing_confirmation', label: 'Missing Confirmation', icon: 'pi-exclamation-triangle', type: 'boolean' },
];

// Active/selected filters
const selectedFilters = ref([]);

// Sorting state
const sortField = ref('register_date');
const sortOrder = ref(-1); // -1 for desc, 1 for asc

// Track if filters have been modified but not applied
const filtersModified = ref(false);

// Column definitions
const columns = [
  { name: "external_trans_id", displayName: "Trans ID", filter: true },
  { name: "status", displayName: "Status", filter: true },
  { name: "external_batch_id", displayName: "Payment File ID", filter: true, clickable: true },
  { name: "employer_code", displayName: "Employer", filter: true },
  { name: "claim_number", displayName: "Claim Number", filter: true },
  { name: "carepay_vendor_id", displayName: "Carepay Vendor ID", filter: true, clickable: true },
  { name: "vendor_name", displayName: "Vendor Name", filter: true },
  { name: "vendor_email", displayName: "Vendor Email", filter: true },
  { name: "customer_name", displayName: "Customer Name", filter: true },
  {
    name: "invoice_date",
    displayName: "Invoice Date",
    format: "date",
    filter: true,
  },
  { name: "payment_amount", displayName: "Amount", format: "currency", filter: true },
  { name: "payment_type", displayName: "Payment Method", filter: true },
  { name: "register_date", displayName: "Register Date", format: "date", filter: true },
  {
    name: "confirmation_number",
    displayName: "Payment Confirmation Number",
    filter: true,
  },
  { name: "paid_date", displayName: "Paid Date", format: "date", filter: true },
  { name: "transaction_batch_status", displayName: "Transaction Batch Status", filter: true },
  { name: "bank_payment_file_status", displayName: "Bank Payment File Status", filter: true },
  { name: "dcn", displayName: "Invoice Number", filter: true },
  { name: "eor_link", displayName: "EOR Link", filter: true },
  { name: "payment_processed_date", displayName: "Payment Processed Date", format: "date", filter: true },
];

const filteredColumns = ref([...columns]);
const visibleColumns = computed(() => {
  return filteredColumns.value.filter((column) => !column.hidden);
});

// Count active filters
const activeFilterCount = computed(() => {
  let count = 0;
  if (apiFilters.value.customer_id) count++;
  if (apiFilters.value.customer_name) count++;
  if (apiFilters.value.status) count++;
  if (apiFilters.value.payment_type) count++;
  if (apiFilters.value.carepay_vendor_id) count++;
  if (apiFilters.value.vendor_name) count++;
  if (apiFilters.value.employer) count++;
  if (apiFilters.value.claim_number) count++;
  if (apiFilters.value.external_trans_id) count++;
  if (apiFilters.value.confirmation_number) count++;
  if (apiFilters.value.register_date_from) count++;
  if (apiFilters.value.register_date_to) count++;
  if (apiFilters.value.invoice_date_from) count++;
  if (apiFilters.value.invoice_date_to) count++;
  if (apiFilters.value.payment_amount_min !== null && apiFilters.value.payment_amount_min !== '') count++;
  if (apiFilters.value.payment_amount_max !== null && apiFilters.value.payment_amount_max !== '') count++;
  if (apiFilters.value.external_batch_id) count++;
  if (apiFilters.value.vendor_email) count++;
  if (apiFilters.value.transaction_batch_status) count++;
  if (apiFilters.value.bank_payment_file_status) count++;
  if (apiFilters.value.dcn) count++;
  if (apiFilters.value.eor_link) count++;
  if (apiFilters.value.payment_processed_date_from) count++;
  if (apiFilters.value.payment_processed_date_to) count++;
  // "Has value" boolean filters
  if (apiFilters.value.has_confirmation_number !== null) count++;
  if (apiFilters.value.has_external_batch_id !== null) count++;
  if (apiFilters.value.has_vendor_email !== null) count++;
  if (apiFilters.value.has_dcn !== null) count++;
  if (apiFilters.value.has_eor_link !== null) count++;
  if (apiFilters.value.has_carepay_vendor_id !== null) count++;
  if (apiFilters.value.has_claim_number !== null) count++;
  if (apiFilters.value.has_external_trans_id !== null) count++;
  if (apiFilters.value.missing_confirmation !== null) count++;
  return count;
});

// Filter options
const statusOptions = [
  { label: "All", value: null },
  { label: "Invoiced", value: "Invoiced" },
  { label: "Confirmed", value: "Confirmed" },
  { label: "Processed", value: "Processed" },
  { label: "Unprocessed", value: "Unprocessed" },
];

const paymentTypeOptions = [
  { label: "All", value: null },
  { label: "ACH", value: "ACH" },
  { label: "Check", value: "Check" },
  { label: "Wire", value: "Wire" },
];

const transactionBatchStatusOptions = [
  { label: "All", value: null },
  { label: "New", value: "NEW" },
  { label: "Stuck", value: "STUCK" },
  { label: "Unstuck", value: "UNSTUCK" },
];

const bankPaymentFileStatusOptions = [
  { label: "All", value: null },
  { label: "Processed", value: "PROCESSED" },
  { label: "Unprocessed", value: "UNPROCESSED" },
];

// Methods
const openBatchDetail = (batchId) => {
  if (batchId) {
    selectedBatchId.value = batchId;
    showBatchDetailDialog.value = true;
  }
};

const openVendorDialog = (vendorId) => {
  if (vendorId) {
    selectedVendorId.value = vendorId;
    showVendorDialog.value = true;
  }
};

const toggleColumn = (event) => {
  const toHideColumnNames = event.value;
  for (const column of filteredColumns.value) {
    column.hidden = toHideColumnNames.includes(column.name);
  }
};

const formatter = (value, formatType) => {
  if (formatType === "currency") {
    return formatCurrency(value);
  } else if (formatType === "date") {
    return formatDate(value);
  }
  return value;
};

// Build API params from filters
const buildApiParams = () => {
  const params = {
    page: pagination.value.page,
    pageSize: pagination.value.pageSize,
    sortBy: sortField.value,
    sortOrder: sortOrder.value === 1 ? 'asc' : 'desc',
  };

  // Add filters
  if (apiFilters.value.customer_id) params.customer_id = apiFilters.value.customer_id;
  if (apiFilters.value.customer_name) params.customer_name = apiFilters.value.customer_name;
  if (apiFilters.value.status) params.status = apiFilters.value.status;
  if (apiFilters.value.payment_type) params.payment_type = apiFilters.value.payment_type;
  if (apiFilters.value.carepay_vendor_id) params.carepay_vendor_id = apiFilters.value.carepay_vendor_id;
  if (apiFilters.value.vendor_name) params.vendor_name = apiFilters.value.vendor_name;
  if (apiFilters.value.employer) params.employer = apiFilters.value.employer;
  if (apiFilters.value.claim_number) params.claim_number = apiFilters.value.claim_number;
  if (apiFilters.value.external_trans_id) params.external_trans_id = apiFilters.value.external_trans_id;
  if (apiFilters.value.confirmation_number) params.confirmation_number = apiFilters.value.confirmation_number;
  if (apiFilters.value.external_batch_id) params.external_batch_id = apiFilters.value.external_batch_id;
  if (apiFilters.value.vendor_email) params.vendor_email = apiFilters.value.vendor_email;
  if (apiFilters.value.transaction_batch_status) params.transaction_batch_status = apiFilters.value.transaction_batch_status;
  if (apiFilters.value.bank_payment_file_status) params.bank_payment_file_status = apiFilters.value.bank_payment_file_status;
  if (apiFilters.value.dcn) params.dcn = apiFilters.value.dcn;
  if (apiFilters.value.eor_link) params.eor_link = apiFilters.value.eor_link;

  // Date filters
  if (apiFilters.value.register_date_from) {
    params.register_date_from = formatDateForAPI(apiFilters.value.register_date_from);
  }
  if (apiFilters.value.register_date_to) {
    params.register_date_to = formatDateForAPI(apiFilters.value.register_date_to);
  }
  if (apiFilters.value.invoice_date_from) {
    params.invoice_date_from = formatDateForAPI(apiFilters.value.invoice_date_from);
  }
  if (apiFilters.value.invoice_date_to) {
    params.invoice_date_to = formatDateForAPI(apiFilters.value.invoice_date_to);
  }
  if (apiFilters.value.payment_processed_date_from) {
    params.payment_processed_date_from = formatDateForAPI(apiFilters.value.payment_processed_date_from);
  }
  if (apiFilters.value.payment_processed_date_to) {
    params.payment_processed_date_to = formatDateForAPI(apiFilters.value.payment_processed_date_to);
  }

  // Amount filters (convert to cents)
  if (apiFilters.value.payment_amount_min !== null && apiFilters.value.payment_amount_min !== '') {
    params.payment_amount_min = Math.round(apiFilters.value.payment_amount_min * 100);
  }
  if (apiFilters.value.payment_amount_max !== null && apiFilters.value.payment_amount_max !== '') {
    params.payment_amount_max = Math.round(apiFilters.value.payment_amount_max * 100);
  }

  // "Has value" boolean filters
  if (apiFilters.value.has_confirmation_number !== null) params.has_confirmation_number = apiFilters.value.has_confirmation_number;
  if (apiFilters.value.has_external_batch_id !== null) params.has_external_batch_id = apiFilters.value.has_external_batch_id;
  if (apiFilters.value.has_vendor_email !== null) params.has_vendor_email = apiFilters.value.has_vendor_email;
  if (apiFilters.value.has_dcn !== null) params.has_dcn = apiFilters.value.has_dcn;
  if (apiFilters.value.has_eor_link !== null) params.has_eor_link = apiFilters.value.has_eor_link;
  if (apiFilters.value.has_carepay_vendor_id !== null) params.has_carepay_vendor_id = apiFilters.value.has_carepay_vendor_id;
  if (apiFilters.value.has_claim_number !== null) params.has_claim_number = apiFilters.value.has_claim_number;
  if (apiFilters.value.has_external_trans_id !== null) params.has_external_trans_id = apiFilters.value.has_external_trans_id;
  if (apiFilters.value.missing_confirmation !== null) params.missing_confirmation = apiFilters.value.missing_confirmation;

  return params;
};

const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Load transactions from API
const loadTransactions = async () => {
  loading.value = true;
  try {
    const params = buildApiParams();
    const response = await dashboardAPI.getTransactionsPaginated(params);

    transactions.value = response.data.data || [];
    pagination.value = {
      page: response.data.pagination.page,
      pageSize: response.data.pagination.pageSize,
      totalItems: response.data.pagination.totalItems,
      totalPages: response.data.pagination.totalPages,
    };
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch transactions",
      life: 3000,
    });
    console.error("Error fetching transactions:", error);
  } finally {
    loading.value = false;
  }
};

// Load sum from API
const loadSum = async () => {
  try {
    const params = buildApiParams();
    // Remove pagination params for sum
    delete params.page;
    delete params.pageSize;
    delete params.sortBy;
    delete params.sortOrder;

    const response = await dashboardAPI.getTransactionsSum(params);
    totalSum.value = response.data.sum || 0;
  } catch (error) {
    console.error("Error fetching transaction sum:", error);
    totalSum.value = 0;
  }
};

// Handle page change
const onPage = (event) => {
  pagination.value.page = event.page + 1; // PrimeVue uses 0-based pages
  pagination.value.pageSize = event.rows;
  loadTransactions();
};

// Handle sort change
const onSort = (event) => {
  sortField.value = event.sortField;
  sortOrder.value = event.sortOrder;
  loadTransactions();
};

// Mark filters as modified (not yet applied)
const onFilterModified = () => {
  filtersModified.value = true;
};

// Apply filters - actually fetch data with current filter values
const applyFilters = () => {
  filtersModified.value = false;
  pagination.value.page = 1; // Reset to first page when filtering
  loadTransactions();
  loadSum();
};

// Legacy method for backward compatibility (now just applies filters)
const onFilterChange = () => {
  applyFilters();
};

const clearFilters = () => {
  apiFilters.value = {
    customer_id: '',
    customer_name: '',
    status: null,
    payment_type: null,
    carepay_vendor_id: '',
    vendor_name: '',
    employer: '',
    claim_number: '',
    external_trans_id: '',
    confirmation_number: '',
    register_date_from: null,
    register_date_to: null,
    invoice_date_from: null,
    invoice_date_to: null,
    payment_amount_min: null,
    payment_amount_max: null,
    external_batch_id: '',
    vendor_email: '',
    transaction_batch_status: null,
    bank_payment_file_status: null,
    dcn: '',
    eor_link: '',
    payment_processed_date_from: null,
    payment_processed_date_to: null,
    // "Has value" filters
    has_confirmation_number: null,
    has_external_batch_id: null,
    has_vendor_email: null,
    has_dcn: null,
    has_eor_link: null,
    has_carepay_vendor_id: null,
    has_claim_number: null,
    has_external_trans_id: null,
    missing_confirmation: null,
  };
  selectedFilters.value = [];
  applyFilters();
};

// Remove a specific filter
const removeFilter = (filterKey) => {
  selectedFilters.value = selectedFilters.value.filter(f => f !== filterKey);
  // Clear the filter value
  if (filterKey in apiFilters.value) {
    if (typeof apiFilters.value[filterKey] === 'string') {
      apiFilters.value[filterKey] = '';
    } else {
      apiFilters.value[filterKey] = null;
    }
  }
  onFilterChange();
};

// Get filter configuration by key
const getFilterConfig = (key) => {
  return availableFilters.find(f => f.key === key);
};


// Download CSV with current filters
const downloadCSV = async () => {
  loading.value = true;
  try {
    const params = buildApiParams();

    // Remove pagination for CSV export
    delete params.page;
    delete params.pageSize;
    delete params.sortBy;
    delete params.sortOrder;

    // Call API with authentication
    const response = await dashboardAPI.downloadTransactionsCSV(params);

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.download = `transactions_${timestamp}.csv`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.add({
      severity: "success",
      summary: "Success",
      detail: "CSV file downloaded successfully",
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to download CSV file",
      life: 3000,
    });
    console.error("Error downloading CSV:", error);
  } finally {
    loading.value = false;
  }
};

// Load data on mount
onMounted(() => {
  loadTransactions();
  loadSum();
});
</script>

<template>
  <div class="transactions-view">
    <!-- Summary Bar -->
    <div class="summary-bar">
      <div class="summary-card">
        <div class="summary-icon">
          <i class="pi pi-dollar"></i>
        </div>
        <div class="summary-content">
          <span class="summary-label">Total Amount</span>
          <span class="summary-value">{{ formatCurrency(totalSum) }}</span>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon records">
          <i class="pi pi-list"></i>
        </div>
        <div class="summary-content">
          <span class="summary-label">Total Records</span>
          <span class="summary-value">{{ pagination.totalItems.toLocaleString() }}</span>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon filters">
          <i class="pi pi-filter"></i>
        </div>
        <div class="summary-content">
          <span class="summary-label">Active Filters</span>
          <span class="summary-value">{{ activeFilterCount }}</span>
        </div>
      </div>
    </div>

    <!-- Compact Filter Bar -->
    <div class="compact-filter-bar">
      <div class="filter-controls">
        <MultiSelect
          v-model="selectedFilters"
          :options="availableFilters"
          optionLabel="label"
          optionValue="key"
          placeholder="Add filters..."
          display="chip"
          :filter="true"
          class="filter-selector"
        >
          <template #option="slotProps">
            <div class="filter-option">
              <i :class="['pi', slotProps.option.icon, 'mr-2']"></i>
              <span>{{ slotProps.option.label }}</span>
            </div>
          </template>
        </MultiSelect>

        <div class="filter-actions">
          <Button
            v-if="selectedFilters.length > 0"
            label="Apply Filters"
            icon="pi pi-check"
            :class="['p-button-sm', filtersModified ? 'p-button-success' : 'p-button-outlined']"
            :badge="filtersModified ? '!' : null"
            @click="applyFilters"
          />
          <Button
            v-if="activeFilterCount > 0"
            label="Clear All"
            icon="pi pi-filter-slash"
            class="p-button-sm p-button-text p-button-danger"
            @click="clearFilters"
          />
          <Button
            label="Download CSV"
            icon="pi pi-download"
            class="p-button-sm p-button-outlined"
            @click="downloadCSV"
          />
          <MultiSelect
            display="chip"
            :options="columns"
            optionLabel="displayName"
            optionValue="name"
            :filter="true"
            placeholder="Hide columns"
            @change="toggleColumn"
            class="column-selector"
          />
        </div>
      </div>

      <!-- Active Filters with Inline Inputs -->
      <div v-if="selectedFilters.length > 0" class="active-filters">
        <div
          v-for="filterKey in selectedFilters"
          :key="filterKey"
          class="filter-item"
        >
          <div class="filter-item-header">
            <i :class="['pi', getFilterConfig(filterKey)?.icon, 'mr-2']"></i>
            <span class="filter-item-label">{{ getFilterConfig(filterKey)?.label }}</span>
            <Button
              icon="pi pi-times"
              class="p-button-text p-button-sm p-button-rounded remove-filter-btn"
              @click="removeFilter(filterKey)"
            />
          </div>
          <div class="filter-item-input">
            <!-- Status Dropdown -->
            <Dropdown
              v-if="filterKey === 'status'"
              v-model="apiFilters.status"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select status"
              @change="onFilterModified"
              class="w-full"
            />

            <!-- Payment Type Dropdown -->
            <Dropdown
              v-else-if="filterKey === 'payment_type'"
              v-model="apiFilters.payment_type"
              :options="paymentTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select type"
              @change="onFilterModified"
              class="w-full"
            />

            <!-- Transaction Batch Status Dropdown -->
            <Dropdown
              v-else-if="filterKey === 'transaction_batch_status'"
              v-model="apiFilters.transaction_batch_status"
              :options="transactionBatchStatusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select status"
              @change="onFilterModified"
              class="w-full"
            />

            <!-- Bank Payment File Status Dropdown -->
            <Dropdown
              v-else-if="filterKey === 'bank_payment_file_status'"
              v-model="apiFilters.bank_payment_file_status"
              :options="bankPaymentFileStatusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select status"
              @change="onFilterModified"
              class="w-full"
            />

            <!-- Text Inputs -->
            <InputText
              v-else-if="filterKey === 'carepay_vendor_id' || filterKey === 'customer_id' || filterKey === 'customer_name' || filterKey === 'vendor_name' || filterKey === 'employer' || filterKey === 'claim_number' || filterKey === 'external_trans_id' || filterKey === 'confirmation_number' || filterKey === 'external_batch_id' || filterKey === 'vendor_email' || filterKey === 'dcn' || filterKey === 'eor_link'"
              v-model="apiFilters[filterKey]"
              :placeholder="`Enter ${getFilterConfig(filterKey)?.label.toLowerCase()}...`"
              @input="onFilterModified"
              @keyup.enter="applyFilters"
              class="w-full"
            />

            <!-- Date Inputs -->
            <Calendar
              v-else-if="getFilterConfig(filterKey)?.type === 'date'"
              v-model="apiFilters[filterKey]"
              dateFormat="yy-mm-dd"
              placeholder="Select date"
              @date-select="onFilterModified"
              @clear-click="onFilterModified"
              :showButtonBar="true"
              :showIcon="true"
              class="w-full"
            />

            <!-- Currency Inputs -->
            <InputNumber
              v-else-if="getFilterConfig(filterKey)?.type === 'currency'"
              v-model="apiFilters[filterKey]"
              mode="currency"
              currency="USD"
              placeholder="$0.00"
              @input="onFilterModified"
              @keyup.enter="applyFilters"
              class="w-full"
            />

            <!-- Boolean/Checkbox Inputs -->
            <Dropdown
              v-else-if="getFilterConfig(filterKey)?.type === 'boolean'"
              v-model="apiFilters[filterKey]"
              :options="filterKey === 'missing_confirmation' ? [
                { label: 'Any', value: null },
                { label: 'Flagged only', value: true },
                { label: 'Not flagged', value: false }
              ] : [
                { label: 'Any', value: null },
                { label: 'Has Value', value: true },
                { label: 'Is Empty', value: false }
              ]"
              optionLabel="label"
              optionValue="value"
              placeholder="Select option"
              @change="onFilterModified"
              class="w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <DataTable
      :value="transactions"
      :loading="loading"
      lazy
      paginator
      :rows="pagination.pageSize"
      :totalRecords="pagination.totalItems"
      :rowsPerPageOptions="[10, 25, 50, 100]"
      @page="onPage"
      @sort="onSort"
      :sortField="sortField"
      :sortOrder="sortOrder"
      stripedRows
      dataKey="id"
      :size="'small'"
    >
      <Column
        v-for="column in visibleColumns"
        :key="column.name"
        :field="column.name"
        :header="column.displayName"
        :sortable="true"
      >
        <template v-if="column.name === 'status'" #body="slotProps">
          <span class="status-cell">
            {{ slotProps.data.status }}
            <i
              v-if="slotProps.data.missing_confirmation"
              class="pi pi-exclamation-triangle missing-confirmation-icon"
              v-tooltip.top="'Missing confirmation number — payment may need review'"
            ></i>
          </span>
        </template>
        <template v-else-if="column.clickable && column.name === 'external_batch_id'" #body="slotProps">
          <Button
            :label="slotProps.data[column.name] || 'N/A'"
            link
            @click="openBatchDetail(slotProps.data['transaction_batch_id'])"
            class="batch-link"
          />
        </template>
        <template v-else-if="column.clickable && column.name === 'carepay_vendor_id'" #body="slotProps">
          <a
            v-if="slotProps.data['vendor_id']"
            href="javascript:void(0)"
            class="vendor-link"
            @click="openVendorDialog(slotProps.data['vendor_id'])"
          >
            {{ slotProps.data[column.name] }}
          </a>
          <span v-else>N/A</span>
        </template>
        <template v-else-if="column.format" #body="slotProps">
          {{ formatter(slotProps.data[column.name], column.format) }}
        </template>
      </Column>
    </DataTable>

    <!-- Dialogs -->
    <Dialog
      v-model:visible="showBatchDetailDialog"
      modal
      :style="{ width: '90vw', maxWidth: '1200px' }"
      :header="`Transaction Batch Details - ${selectedBatchId}`"
      :closable="true"
      :dismissableMask="true"
    >
      <TransactionBatchDetailView
        v-if="selectedBatchId"
        :batchId="selectedBatchId"
      />
    </Dialog>

    <VendorDetailDialog
      v-model:visible="showVendorDialog"
      :vendorId="selectedVendorId"
    />
  </div>
</template>

<style scoped>
.transactions-view {
  width: 100%;
}

/* Summary Bar Styles */
.summary-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.summary-card {
  background: linear-gradient(135deg, var(--p-primary-50) 0%, var(--p-primary-100) 100%);
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid var(--p-primary-200);
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: var(--p-primary-500);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.summary-icon.records {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.summary-icon.filters {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.summary-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--p-text-color);
  line-height: 1;
}

/* Compact Filter Bar */
.compact-filter-bar {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-border);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.filter-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filter-selector {
  flex: 1;
  min-width: 250px;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.column-selector {
  min-width: 200px;
}

.filter-option {
  display: flex;
  align-items: center;
}

/* Active Filters Grid */
.active-filters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-border);
}

.filter-item {
  background: var(--p-surface-50);
  border: 1px solid var(--p-surface-border);
  border-radius: 6px;
  padding: 0.75rem;
  transition: all 0.2s ease;
}

.filter-item:hover {
  border-color: var(--p-primary-300);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.filter-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.25rem;
}

.filter-item-label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.filter-item-header i {
  color: var(--p-primary-500);
  font-size: 0.875rem;
}

.remove-filter-btn {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  color: var(--p-text-muted-color);
}

.remove-filter-btn:hover {
  color: var(--p-danger-500);
  background: var(--p-danger-50);
}

.filter-item-input {
  width: 100%;
}

/* Status cell with flag */
.status-cell {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.missing-confirmation-icon {
  color: #f59e0b;
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* Table Links */
.batch-link {
  color: var(--p-primary-500);
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
}

.batch-link:hover {
  color: var(--p-primary-700);
}

.vendor-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.vendor-link:hover {
  color: #1565c0;
  text-decoration: underline;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .summary-bar {
    grid-template-columns: 1fr;
  }

  .summary-value {
    font-size: 1.25rem;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-selector {
    min-width: 100%;
  }

  .column-selector {
    min-width: 100%;
  }

  .active-filters {
    grid-template-columns: 1fr;
  }
}

/* Apply Filters Button Highlight */
.p-button-success {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .summary-card {
    background: linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.15) 100%);
    border-color: rgba(96, 165, 250, 0.2);
  }

  .filter-item {
    background: rgba(0, 0, 0, 0.2);
  }
}
</style>
